import * as z from "zod";
import { redoCartLineSchema } from "./events/storefront-event-primitives";

/**
 * Wire payloads for the generic widget event intake (POST /widgets/:id/events).
 *
 * This is the single source of truth for what each widget event carries over
 * the wire. Both the browser emitter (`emitWidgetEvent`) and the server intake
 * validate against these schemas, so the two can't drift. Adding a new widget
 * event starts here: add a payload schema + a registry entry in
 * `@redotech/redo-customer-db-util/widget-event-registry`.
 *
 * Lives in the model package (not customer-db-util) so the browser can import
 * payload types without pulling server DB code into the bundle.
 */

export const trackingPageViewedPayloadSchema = z.object({
  // The tracking page the flow conditionally selected to render.
  trackingPageId: z.string(),
  // The URL the customer landed on.
  url: z.string(),
  // Whether the page rendered inside the embedded order-tracking iframe.
  embedded: z.boolean(),
  // The order being tracked, when known.
  orderId: z.string().optional(),
  // The fulfillment/shipment the tracking page is scoped to, when resolved.
  fulfillmentId: z.string().optional(),
  // The carrier tracking number for that shipment, when present.
  trackingNumber: z.string().optional(),
});

export const trackingPageClickedPayloadSchema = z.object({
  // What was clicked: "ad" (promotion), "upsell" (product), "subscription".
  clickType: z.string(),
  // The URL the click navigated to.
  url: z.string(),
  // The order being tracked, when known.
  orderId: z.string().optional(),
  // The tracking page the flow selected, when known.
  trackingPageId: z.string().optional(),
  // The creative image associated with the clicked element, when present.
  image: z.string().optional(),
  // Whether the page rendered inside the embedded order-tracking iframe.
  embedded: z.boolean().optional(),
  // The fulfillment/shipment the tracking page is scoped to, when resolved.
  fulfillmentId: z.string().optional(),
  // The carrier tracking number for that shipment, when present.
  trackingNumber: z.string().optional(),
});

// Generic custom event: arbitrary name + free-form properties, plus optional
// commerce context (value and cart line items). Maps to CUSTOM_EVENT. This is
// the single entry that handles ALL custom events — the arbitrary event
// identity lives in `eventName`, not in a registry key.
export const customEventPayloadSchema = z.object({
  eventName: z.string(),
  properties: z.record(z.string(), z.unknown()).optional(),
  value: z.number().optional(),
  valueCurrency: z.string().optional(),
  lineItems: z.array(redoCartLineSchema).optional(),
});

export const widgetEventPayloads = {
  tracking_page_viewed: trackingPageViewedPayloadSchema,
  tracking_page_clicked: trackingPageClickedPayloadSchema,
  custom: customEventPayloadSchema,
} as const;

export type WidgetEventName = keyof typeof widgetEventPayloads;

export type WidgetEventPayload<N extends WidgetEventName> = z.infer<
  (typeof widgetEventPayloads)[N]
>;

export function isWidgetEventName(name: string): name is WidgetEventName {
  return Object.prototype.hasOwnProperty.call(widgetEventPayloads, name);
}
